import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import BottomNav from "../components/bottomnav/BottomNav";
import { useAuth } from "../context/AuthContext";
import { useSeason } from "../context/SeasonContext";
import { useSettings } from "../context/SettingsContext";

import logo from "../assets/images/logo.png";

const SessionGuard = () => {
  const { sessionError } = useAuth();
  const navigate = useNavigate();
  if (!sessionError) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white text-center py-3 px-4 flex items-center justify-center gap-3 shadow-lg">
      <span className="text-sm font-medium">⚠️ {sessionError}</span>
      <button
        onClick={() => navigate("/login")}
        className="text-sm underline font-bold hover:text-red-200 transition-colors"
      >
        Log in again
      </button>
    </div>
  );
};

const SponsorLogo = ({ sponsor }) => {
  const content = sponsor.logo_url ? (
    <img
      src={sponsor.logo_url}
      alt={sponsor.name}
      className="h-8 max-w-[120px] object-contain transition-all duration-300 group-hover:scale-105"
    />
  ) : (
    <span className="text-xs font-bold tracking-widest uppercase text-gray-500 dark:text-gray-400 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-full transition-all duration-300 group-hover:border-orange-400 group-hover:text-orange-500 dark:group-hover:text-orange-400">
      {sponsor.name}
    </span>
  );

  return (
    <a
      href={sponsor.website_url || "#"}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity duration-300"
      title={sponsor.name}
    >
      {content}
    </a>
  );
};

const MainLayout = () => {
  const { sponsors } = useSeason();
  const { settings, isRegistrationOpen } = useSettings();
  const currentYear = new Date().getFullYear();
  const rulesUrl = settings?.rules_url || null;
  const registrationOpen = isRegistrationOpen();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors flex flex-col overflow-x-hidden">
      <SessionGuard />
      <Navigation />

      {/* Extra bottom padding on mobile so content clears the fixed dock */}
      <main className="flex-1 w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-[1600px] pb-28 lg:pb-8 overflow-x-hidden">
        <Outlet />
      </main>

      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 mt-auto">
        {/* Sponsors strip */}
        {sponsors.length > 0 && (
          <div className="border-b border-gray-100 dark:border-gray-800 py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center gap-6">
              <span className="flex-shrink-0 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                Proudly Sponsored By
              </span>
              <div className="hidden md:block h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-300 to-transparent dark:from-gray-700 dark:via-gray-600" />
              <div className="flex items-center justify-center flex-wrap gap-8">
                {sponsors.map((s) => (
                  <SponsorLogo key={s.id} sponsor={s} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Brand */}
          <div className="flex items-center gap-2.5">
            <img src={logo} alt="Muqalab Hoops" className="h-6 w-6"></img>
            <span className="font-black text-sm tracking-tight text-gray-800 dark:text-gray-200">
              Muqalab Hoops
            </span>
          </div>

          {/* Nav links — Rules added here */}
          <nav className="flex items-center gap-5 text-xs text-gray-500 dark:text-gray-400">
            <a
              href="/schedule"
              className="hover:text-orange-500 transition-colors"
            >
              Schedule
            </a>
            <a
              href="/standings"
              className="hover:text-orange-500 transition-colors"
            >
              Standings
            </a>
            <a
              href="/stats"
              className="hover:text-orange-500 transition-colors"
            >
              Stats
            </a>
            {registrationOpen && (
              <a
                href="/register"
                className="hover:text-orange-500 transition-colors"
              >
                Register
              </a>
            )}
            {rulesUrl && (
              <a
                href={rulesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition-colors"
              >
                League Rules
              </a>
            )}
          </nav>

          {/* Copyright */}
          <p className="text-xs text-gray-400 dark:text-gray-500">
            © {currentYear} Muqalab Hoops
          </p>
        </div>
      </footer>

      {/* Mobile bottom navigation — hidden on lg+ screens */}
      <BottomNav />
    </div>
  );
};

export default MainLayout;
